import defaultReducers from '@src/models/defaultReducers'
import {
  BillingAllowance,
  CoachAssignmentCollection,
  CoachAssignmentSupportType,
  CoachAssignmentState,
  NoteCollection,
  EhrAppointmentRecurrence,
  InsuranceCardSubmission,
  EhrSlot,
  EhrAppointmentCollection,
  Suggested,
  NextRecurringAppointmentSlot,
  EhrAppointmentSchedulingInfoCollection,
} from '@src/types'
import { CoachProfileQuery } from '@src/graphql/fetchCoachProfile.generated'
import { AllCoachProfilesQuery } from '@src/graphql/fetchAllCoachProfiles.generated'
import { EhrAppointmentsQuery } from '../graphql/ehrAppointments.generated'
import { LatestEhrAppointmentCustomType } from '../utils'
import { NutritionistHubStoreState } from './nutritionistHub.types'

export const reducers = {
  ...defaultReducers,
  updateLastUserEhrAppointment: (
    state: NutritionistHubStoreState,
    { payload }: { payload: LatestEhrAppointmentCustomType },
  ) => {
    return {
      ...state,
      latestUserEhrAppointment: payload,
    }
  },
  updateInsurancePolicy: (state: NutritionistHubStoreState, { payload }: { payload: any }) => {
    return {
      ...state,
      insurancePolicy: payload,
    }
  },
  updatePriorityOrderedCoachAssignment: (
    state: NutritionistHubStoreState,
    { payload }: { payload: CoachAssignmentCollection },
  ) => {
    const supportTypePriorityOrder = {
      [CoachAssignmentSupportType.VideoCall]: 1,
      [CoachAssignmentSupportType.AsyncChat]: 2,
      [CoachAssignmentSupportType.VideoChat]: 3,
      [CoachAssignmentSupportType.MealPlan]: 4,
      [CoachAssignmentSupportType.SupplementReview]: 5,
    }

    const statePriorityOrder = {
      [CoachAssignmentState.Inactive]: 1,
      [CoachAssignmentState.Paused]: 2,
      [CoachAssignmentState.Pending]: 3,
      [CoachAssignmentState.Active]: 4,
    }

    const priorityOrderedCoachAssignments = payload.coachAssignments.sort(
      (a, b) =>
        statePriorityOrder[b.state] - statePriorityOrder[a.state] ||
        supportTypePriorityOrder[b.supportType] - supportTypePriorityOrder[a.supportType],
    )

    return {
      ...state,
      priorityOrderedCoachAssignments,
    }
  },
  updatedMemberNotes: (
    state: NutritionistHubStoreState,
    { payload }: { payload: NoteCollection },
  ) => {
    return {
      ...state,
      allMemberNotes: payload.notes,
    }
  },
  updateMemberNotesUpdatedAtTimestamp: (
    state: NutritionistHubStoreState,
    { payload }: { payload: string },
  ) => {
    return {
      ...state,
      memberNotesUpdatedAt: payload,
    }
  },
  updateUpcomingEhrAppointments: (
    state: NutritionistHubStoreState,
    { payload }: { payload: EhrAppointmentCollection },
  ) => {
    return {
      ...state,
      upcomingEhrAppointments: payload.appointments as EhrAppointmentsQuery['ehrAppointments']['appointments'],
    }
  },
  updatePastEhrAppointments: (
    state: NutritionistHubStoreState,
    { payload }: { payload: EhrAppointmentCollection },
  ) => {
    return {
      ...state,
      pastEhrAppointments: payload.appointments as EhrAppointmentsQuery['ehrAppointments']['appointments'],
    }
  },
  updateAllEhrAppointments: (
    state: NutritionistHubStoreState,
    { payload }: { payload: EhrAppointmentCollection },
  ) => {
    return {
      ...state,
      allEhrAppointments: payload.appointments as EhrAppointmentsQuery['ehrAppointments']['appointments'],
    }
  },
  updatePendingEhrAppointments: (
    state: NutritionistHubStoreState,
    { payload }: { payload: EhrAppointmentCollection },
  ) => {
    return {
      ...state,
      pendingEhrAppointments: payload.appointments as EhrAppointmentsQuery['ehrAppointments']['appointments'],
    }
  },
  updateEhrSuggestedAvailability: (
    state: NutritionistHubStoreState,
    { payload }: { payload: Suggested },
  ) => {
    return {
      ...state,
      ehrSuggestedAvailability: payload,
    }
  },
  updateAppointmentRecurrence: (
    state: NutritionistHubStoreState,
    { payload }: { payload: EhrAppointmentRecurrence | null },
  ) => {
    return {
      ...state,
      appointmentRecurrence: payload,
    }
  },
  updateNextRecurringAppointmentSlot: (
    state: NutritionistHubStoreState,
    { payload }: { payload: NextRecurringAppointmentSlot | null },
  ) => {
    return {
      ...state,
      nextRecurringAppointmentSlot: payload,
    }
  },
  updateCoachingCallAllowance: (
    state: NutritionistHubStoreState,
    { payload }: { payload: BillingAllowance | null },
  ) => {
    return {
      ...state,
      coachingCallAllowance: payload,
    }
  },
  updateInsuranceCardSubmission: (
    state: NutritionistHubStoreState,
    { payload }: { payload: InsuranceCardSubmission | null },
  ) => {
    return {
      ...state,
      insuranceCardSubmission: payload,
    }
  },
  updateEhrEarlierAvailableSlots: (
    state: NutritionistHubStoreState,
    { payload }: { payload: { appointmentId: string; slots: EhrSlot[] } },
  ) => {
    return {
      ...state,
      ehrEarlierAvailableSlotsByAppointmentId: { [payload.appointmentId]: payload.slots },
    }
  },
  updateEhrSchedulingConfig: (
    state: NutritionistHubStoreState,
    { payload: ehrAppointmentSchedulingInfo }: { payload: EhrAppointmentSchedulingInfoCollection },
  ): NutritionistHubStoreState => {
    return {
      ...state,
      ehrAppointmentSchedulingInfo,
    }
  },
  updateAllCoachProfiles: (
    state: NutritionistHubStoreState,
    { payload }: { payload: AllCoachProfilesQuery['allCoachProfiles'] },
  ) => {
    return {
      ...state,
      coachProfiles: payload.coachProfiles,
    }
  },
  updateCoachProfile: (
    state: NutritionistHubStoreState,
    { payload }: { payload: CoachProfileQuery['coachProfile'] },
  ) => {
    return {
      ...state,
      coachProfile: payload,
    }
  },
}

export default reducers
